import { getLegacyStorageKey } from '../../utils/storage';
import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useTdsCommon } from '../../hooks/useTdsCommon'
import { getStateCode, formatDate, formatDecimalAmount, padString, padNumber, stateCodes, reverseStateCodes, getTdsFormRoute } from '../../utils/tdsCommon'
import { extractProfileDataApi, downloadCsiFileApi } from '../../utils/tdsFileHandlers'
import TdsProfileSection from '../../components/dashboard/TdsProfileSection'
import { getStorageItem, setStorageItem } from '../../utils/storage'
import { form143Sections, minorHeadCodes143, collectorTypes143 } from '../../utils/tds143Constants'

import { motion } from 'framer-motion'
import { FileText, User, Upload, ArrowLeft, Building2, X, Trash2, Eye, EyeOff } from 'lucide-react'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast, ToastContainer } from 'react-toastify'
import axios from 'axios'
import TdsDetailsTable from '../../components/dashboard/TdsDetailsTable'
import ChallanDetailsTable from '../../components/dashboard/ChallanDetailsTable'
import TdsSidebar from '../../components/dashboard/TdsSidebar'
import TdsGeneratedFiles from './TdsGeneratedFiles'
import { useError } from '../../contexts/ErrorContext'

const stateOptions = [
    'ANDAMAN AND NICOBAR ISLANDS', 'ANDHRA PRADESH', 'ARUNACHAL PRADESH', 'ASSAM', 'BIHAR', 'CHANDIGARH',
    'CHHATTISGARH', 'DADRA & NAGAR HAVELI AND DAMAN & DIU', 'DELHI', 'GOA', 'GUJARAT', 'HARYANA',
    'HIMACHAL PRADESH', 'JAMMU & KASHMIR', 'JHARKHAND', 'KARNATAKA', 'KERALA', 'LADAKH', 'LAKSHWADEEP',
    'MADHYA PRADESH', 'MAHARASHTRA', 'MANIPUR', 'MEGHALAYA', 'MIZORAM', 'NAGALAND', 'ODISHA',
    'PONDICHERRY', 'PUNJAB', 'RAJASTHAN', 'SIKKIM', 'TAMIL NADU', 'TELANGANA', 'TRIPURA',
    'UTTAR PRADESH', 'UTTARAKHAND', 'WEST BENGAL'
];

// COLLECTEE CODES FOR FORM 143 (TCS)
const collecteeCodes143 = [
    { value: '01', label: '01 - Company, other than domestic company' },
    { value: '02', label: '02 - Individual' },
    { value: '03', label: '03 - Hindu Undivided Family' },
    { value: '04', label: '04 - Association of Persons (AOP)' },
    { value: '05', label: '05 - Association of Persons (AOP) (Companies)' },
    { value: '06', label: '06 - Co-operative Society' },
    { value: '07', label: '07 - Firm' },
    { value: '08', label: '08 - Body of individuals' },
    { value: '09', label: '09 - Artificial juridical person' },
    { value: '10', label: '10 - Others' }
];

const formatMinorHead143 = (val) => {
    if (!val) return ''
    const s = String(val).trim()
    if (s === '200' || s === '400') return s
    const lower = s.toLowerCase()
    if (lower.includes('payable') || lower.includes('taxpayer')) return '200'
    if (lower.includes('regular') || lower.includes('assessment')) return '400'
    const match = s.match(/(200|400)/)
    if (match) return match[0]
    return s
}

const Tds143Form = ({ isMobile, onSidebarStateChange }) => {
    const navigate = useNavigate()
    const location = useLocation()
    const { addError } = useError()
    const [activeSection, setActiveSection] = useState('profile')
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const [sidebarState, setSidebarState] = useState({
        isOpen: false,
        isExpanded: false,
        keepOpen: false,
        hovered: false
    })

    // Custom Validation Function for Form 143
    function validateDataForReturn() {
        const errors = []
        const pData = profileData
        const cData = challanData
        const tData = tdsData

        if (!pData?.section1?.taxDeduction) errors.push('TAN of Collector is required in Profile')
        if (!pData?.section1?.permanentAccountNumber) errors.push('PAN of Collector is required in Profile')
        if (!pData?.section2?.name) errors.push('Collector Name is required in Profile')

        if (!cData || cData.length === 0) {
            errors.push('At least one Challan Record is required')
        }

        if (!tData || tData.length === 0) {
            errors.push('At least one Collectee/Party Record is required')
        } else {
            tData.forEach((row, idx) => {
                const optOut = row.optOutRegime || row.optOutTaxation || row.optOut115BAC;
                if (!optOut || (optOut !== 'Y' && optOut !== 'N')) {
                    errors.push(`Row ${idx + 1} in Party Details table: 'Opting Out New Regime' selection ('Yes' or 'No') is required.`);
                }
            });
        }

        return errors
    }

    // Custom e-TCS / Form 143 File Generation
    function generateTdsStatementFile(customPData, customCData, customTData, options = {}) {
        const pData = customPData || profileData
        const cData = customCData || challanData
        const tData = customTData || tdsData
        const lines = []
        let lineNumber = 1

        const section1 = pData?.section1 || {}
        const section2 = pData?.section2 || {}
        const section3 = pData?.section3 || {}

        const fy = options.financialYear || selectedFinancialYear || '2026-27'
        const parts = fy.split('-')
        const startYr = parseInt(parts[0], 10)
        const fyStartYear = isNaN(startYr) ? 2026 : startYr
        const endYr2Digit = parts[1] || '27'
        const endYrFull = parseInt(endYr2Digit, 10) < 100 ? (2000 + parseInt(endYr2Digit, 10)) : parseInt(endYr2Digit, 10)

        const financialYear = `${fyStartYear}${String(endYrFull).slice(-2)}`
        const assessmentYear = `${fyStartYear + 1}${String(endYrFull + 1).slice(-2)}`

        const totalChallanAmt = (cData || []).reduce((acc, c) => {
            const tax = parseFloat(c.tdsAmount || c.tax || c.totalTaxDeposited || c.totalTaxDeducted || c.challanAmount || 0) || 0
            const int = parseFloat(c.interest || 0) || 0
            const fee = parseFloat(c.feesAmount || c.fee || 0) || 0
            const pen = parseFloat(c.other || c.penalty || 0) || 0
            const tot = parseFloat(c.totalAmount || c.totalTaxDeposited || (tax + int + fee + pen)) || 0
            return acc + tot
        }, 0)

        const today = new Date()
        const dd = String(today.getDate()).padStart(2, '0')
        const mm = String(today.getMonth() + 1).padStart(2, '0')
        const yyyy = today.getFullYear()
        const fileCreationDate = `${dd}${mm}${yyyy}`

        // File Header (FH) Record
        const fh = []
        fh[1] = lineNumber
        fh[2] = 'FH'
        fh[3] = 'TC1'
        fh[4] = 'R'
        fh[5] = fileCreationDate
        fh[6] = '1'
        fh[7] = 'D'
        fh[8] = section1.taxDeduction || ''
        fh[9] = '1'
        fh[10] = 'Tax Eases Client'
        fh[11] = ''
        fh[12] = ''
        fh[13] = ''
        fh[14] = ''
        fh[15] = ''
        fh[16] = ''
        fh[17] = ''
        fh[18] = ''

        lines.push(fh.slice(1).join('^'))
        lineNumber++

        // Batch Header (BH) Record
        const dAddr1 = `${section2.flatNo || ''} ${section2.nameOfPremises || ''} ${section2.roadStreet || ''}`.trim()
        const rAddr1 = `${section3.flatNo || ''} ${section3.nameOfPremises || ''} ${section3.roadStreet || ''}`.trim()

        const bh = []
        bh[1] = lineNumber
        bh[2] = 'BH'
        bh[3] = '1'
        bh[4] = String((cData || []).length)
        bh[5] = '143'
        bh[6] = ''
        bh[7] = ''
        bh[8] = ''
        bh[9] = section1.receiptNoEarlier || ''
        bh[10] = ''
        bh[11] = ''
        bh[12] = ''
        bh[13] = section1.taxDeduction || ''
        bh[14] = ''
        bh[15] = section1.permanentAccountNumber || ''
        bh[16] = assessmentYear
        bh[17] = financialYear
        bh[18] = selectedQuarter || options.quarter || 'Q1'; // 18. Period
        bh[19] = padString(section2.name, 75)
        bh[20] = padString(section2.country || 'INDIA', 25)
        bh[21] = padString(dAddr1, 25)
        bh[22] = padString(section2.areaLocality, 25)
        bh[23] = padString(section2.townCity, 25)
        bh[24] = padString(section2.state, 25)
        bh[25] = ''
        bh[26] = getStateCode(section2.state)
        bh[27] = section2.pinCode || ''
        bh[28] = padString(section2.email, 75)
        bh[29] = section2.stdCode || '91'
        bh[30] = section2.telephoneNo || ''
        bh[31] = ''
        bh[32] = section1.typeOfDeduction || 'K'
        bh[33] = padString(section3.name, 75)
        bh[34] = padString(section3.designation, 20)
        bh[35] = padString(rAddr1, 25)
        bh[36] = padString(section3.areaLocality, 25)
        bh[37] = padString(section3.townCity, 25)
        bh[38] = ''
        bh[39] = ''
        bh[40] = getStateCode(section3.state)
        bh[41] = section3.pinCode || ''
        bh[42] = padString(section3.email, 75)
        bh[43] = section3.stdCode || '91'
        bh[44] = section3.stdCode || '91'
        bh[45] = section3.mobileNo || section3.telephone || section3.telephoneNo || ''
        bh[46] = ''
        bh[47] = formatDecimalAmount(totalChallanAmt)
        bh[48] = ''
        bh[49] = ''
        bh[50] = ''
        bh[51] = ''
        bh[52] = section1.hasFiledEarlier ? 'Y' : 'N'
        bh[53] = ''
        bh[54] = ''
        bh[55] = ''
        bh[56] = ''
        bh[57] = ''
        bh[58] = ''
        bh[59] = section3.permanentAccountNumber || section3.pan || ''
        bh[60] = ''
        bh[61] = ''
        bh[62] = ''
        bh[63] = ''
        bh[64] = ''
        bh[65] = ''
        bh[66] = ''
        bh[67] = ''
        bh[68] = section1.ainNo || ''
        bh[69] = section1.gstin || ''
        bh[70] = ''
        bh[71] = ''
        bh[72] = ''

        lines.push(bh.slice(1).join('^'))
        lineNumber++

        // Challan Detail (CD) and Collectee Detail (DD) Records
        (cData || []).forEach((c, cIdx) => {
            const challanNo = cIdx + 1
            const underlyingDd = (tData || []).filter(t =>
                String(t.challanId) === String(c.id) ||
                String(t.challanId) === String(challanNo) ||
                String(t.challanNo) === String(challanNo) ||
                String(t.challanRefNo) === String(challanNo)
            )
            const countCollectees = underlyingDd.length

            const taxAmt = parseFloat(c.tdsAmount || c.tax || c.totalTaxDeposited || c.totalTaxDeducted || c.challanAmount || 0) || 0
            const intAmt = parseFloat(c.interest || 0) || 0
            const feeAmt = parseFloat(c.feesAmount || c.fee || 0) || 0
            const penAmt = parseFloat(c.other || c.penalty || 0) || 0
            const totDep = parseFloat(c.totalAmount || c.totalTaxDeposited || (taxAmt + intAmt + feeAmt + penAmt)) || 0

            const totDdDep = underlyingDd.reduce((sum, d) => sum + (parseFloat(d.totalTaxDeposited || d.tdsAmount) || 0), 0)
            const totDdCol = underlyingDd.reduce((sum, d) => sum + (parseFloat(d.totalTaxDeducted || d.tdsAmount) || 0), 0)

            const cd = []
            cd[1] = lineNumber
            cd[2] = 'CD'
            cd[3] = '1'
            cd[4] = String(challanNo)
            cd[5] = String(countCollectees)
            cd[6] = c.nilChallan ? 'Y' : 'N'
            cd[7] = ''
            cd[8] = formatDecimalAmount(taxAmt)
            cd[9] = formatDecimalAmount(intAmt)
            cd[10] = formatDecimalAmount(feeAmt)
            cd[11] = formatDecimalAmount(penAmt)
            cd[12] = formatDecimalAmount(totDep)
            cd[13] = c.modeOfPayment || 'C'
            cd[14] = ''
            cd[15] = padNumber(c.bsrCode || '', 7)
            cd[16] = ''
            cd[17] = padNumber(c.challanSerialNo || c.challanNo || '', 5)
            cd[18] = ''
            cd[19] = formatDate(c.dateOfDeposit || c.challanDate || c.date)
            cd[20] = ''
            cd[21] = formatDecimalAmount(totDdDep)
            cd[22] = formatDecimalAmount(totDdCol)
            cd[23] = formatMinorHead143(c.minorHead) || '200'
            cd[24] = formatDecimalAmount(c.interestAllocation !== undefined && c.interestAllocation !== '' ? c.interestAllocation : (intAmt || 0)) // 24. Interest Allocation
            cd[25] = formatDecimalAmount(c.otherAllocation !== undefined && c.otherAllocation !== '' ? c.otherAllocation : (c.feesAllocation !== undefined && c.feesAllocation !== '' ? c.feesAllocation : (penAmt || c.other || c.penalty || 0))) // 25. Other Allocation
            cd[26] = ''
            cd[27] = ''
            cd[28] = ''
            cd[29] = ''
            cd[30] = ''

            lines.push(cd.slice(1).join('^'))
            lineNumber++

            underlyingDd.forEach((d, dIdx) => {
                const collecteePan = d.panOfDeductee || d.panOfCollectee || d.pan || 'PANNOTAVBL'
                const collecteeName = d.nameOfDeductee || d.collecteeName || d.deducteeName || d.name || `${d.firstName || ''} ${d.middleName || ''} ${d.lastName || ''}`.trim()
                const sectionCode = d.sectionCode || d.section || d.collectionCode || '1068'

                const amtPaid = parseFloat(d.amountOfPayment || d.amountPaid || d.amountDebited || d.totalAmount || d.amount || d.purchaseAmount || d.value || 0) || 0
                const taxDeducted = parseFloat(d.totalTaxDeducted || d.totalTaxDeposited || d.tdsAmount || d.tax || 0) || 0

                let rateVal = parseFloat(d.rateOfTax || d.rate || d.taxRate || d.tdsRate || 0) || 0
                if (!rateVal && amtPaid > 0 && taxDeducted > 0) {
                    rateVal = (taxDeducted / amtPaid) * 100
                }

                const ddRec = []
                ddRec[1] = lineNumber
                ddRec[2] = 'DD'
                ddRec[3] = '1'
                ddRec[4] = String(dIdx + 1)
                ddRec[5] = String(challanNo)
                ddRec[6] = 'O'
                ddRec[7] = ''
                ddRec[8] = padString(String(collecteePan || '').replace(/[\r\n]/g, '').trim(), 10)
                ddRec[9] = padString(String(collecteeName || '').replace(/[\r\n]/g, ' ').replace(/\s+/g, ' ').trim(), 75)
                ddRec[10] = ''
                ddRec[11] = d.isNonResident ? 'Y' : 'N'
                ddRec[12] = d.collecteeCode || '02'
                ddRec[13] = d.hasPEInIndia || ''
                ddRec[14] = d.optOutRegime || d.optOut115BAC || ''
                ddRec[15] = padString(sectionCode, 4)
                ddRec[16] = ''
                ddRec[17] = ''
                ddRec[18] = d.taxDepositedIndicator || 'Y'
                ddRec[19] = formatDate(d.dateOfPayment || d.dateOfDebit || d.dateOfDeduction)
                ddRec[20] = formatDecimalAmount(amtPaid)
                ddRec[21] = ''
                ddRec[22] = ''
                ddRec[23] = ''
                ddRec[24] = formatDecimalAmount(taxDeducted)
                ddRec[25] = formatDecimalAmount(taxDeducted)
                ddRec[26] = ''
                ddRec[27] = formatDate(d.dateOfCollection || d.dateOfDeduction || d.dateOfPayment)
                ddRec[28] = rateVal.toFixed(4)
                ddRec[29] = ''
                ddRec[30] = ''
                ddRec[31] = ''
                const reasonCode = d.reason || d.reasonForNonDeduction || ''
                const cleanReason = String(reasonCode).trim().toUpperCase()
                const secCodeStr = String(sectionCode || '').trim().slice(0, 4)

                const isField34Applicable = cleanReason === 'F' && ['1086', '1087', '1088', '1089'].includes(secCodeStr)

                let sec394_5Val = ''
                if (isField34Applicable) {
                    const rawVal = String(d.sec394_5Indicator || d.isTdsDeductedSec394_5 || '').trim().toUpperCase()
                    sec394_5Val = (rawVal === 'Y' || rawVal === 'YES') ? 'Y' : 'N'
                }

                ddRec[32] = reasonCode
                ddRec[33] = d.certificateNo || d.certificateNo197 || ''
                ddRec[34] = sec394_5Val
                ddRec[35] = sec394_5Val === 'Y' ? (d.sec394_5ChallanNo || d.challanNo || '') : ''
                ddRec[36] = sec394_5Val === 'Y' ? formatDate(d.sec394_5DateOfPayment || d.dateOfPayment) : ''
                ddRec[37] = ''
                ddRec[38] = ''
                ddRec[39] = ''
                ddRec[40] = ''
                ddRec[41] = ''
                ddRec[42] = ''
                ddRec[43] = ''
                ddRec[44] = ''
                ddRec[45] = ''

                lines.push(ddRec.slice(1).join('^'))
                lineNumber++
            })
        })

        return lines.join('\n') + '\n'
    }

    const {
        profileData,
        challanData,
        tdsData,
        originalTdsData,
        csiFile,
        consoFileContent,
        isValidating,
        showCsiUploadPopup,
        showDownloadPrompt,
        selectedFinancialYear,
        selectedQuarter,
        selectedForm,
        selectedReturnType,
        challanDateRange,
        setProfileData,
        setChallanData,
        setTdsData,
        setOriginalTdsData,
        setCsiFile,
        setConsoFileContent,
        setIsValidating,
        setShowCsiUploadPopup,
        setShowDownloadPrompt,
        setSelectedFinancialYear,
        setSelectedQuarter,
        setSelectedForm,
        setSelectedReturnType,
        handlePrepareReturnFile,
        handleValidateReturn,
        handleSkipDownload,
        handleCancelValidation,
        executeValidation
    } = useTdsCommon({
        formName: 'tds143',
        defaultFormType: '143',
        isCorrection: false,
        generateTdsStatementFile,
        validateDataForReturn,
        onValidationError: (errors) => {
            addError('Please fix the following errors before preparing return:')
            errors.forEach(err => addError(`• ${err}`))
            setSidebarOpen(true)
            setActiveSection('errors')
        }
    });

    const [isProfileEditing, setIsProfileEditing] = useState(true)
    const [downloadPassword, setDownloadPassword] = useState('')
    const [showDownloadPassword, setShowDownloadPassword] = useState(false)
    const [isDownloadingCsi, setIsDownloadingCsi] = useState(false)
    const csiFileInputRef = useRef(null)

    const [showPrefillPrompt, setShowPrefillPrompt] = useState(false);
    const [prefillPassword, setPrefillPassword] = useState('');
    const [showPrefillPassword, setShowPrefillPassword] = useState(false);
    const [isExtractingProfile, setIsExtractingProfile] = useState(false);

    const handleCsiFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setCsiFile(e.target.files[0])
            setShowCsiUploadPopup(false)
            executeValidation(e.target.files[0])
        }
    }

    const handleCsiDownload = async () => {
        if (!downloadPassword) {
            toast.error('Please enter Income Tax Portal Password');
            return;
        }

        setIsDownloadingCsi(true);
        try {
            const { from, to } = challanDateRange;
            if (!from || !to) {
                toast.error('Could not determine date range from challans. Please upload CSI manually.');
                setIsDownloadingCsi(false);
                return;
            }

            const response = await axios.post('/api/login-income-tax', {
                credentials: {
                    tan: profileData?.section1?.taxDeduction || profileData?.section1?.tan,
                    password: downloadPassword
                },
                dateRange: { from, to }
            });

            if (response.data.success && response.data.csiContent) {
                toast.success('CSI File Downloaded Successfully!');
                setShowDownloadPrompt(false);
                if (typeof setDownloadPassword === 'function') setDownloadPassword('');

                const binaryString = window.atob(response.data.csiContent.replace(/\s/g, ''));
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }
                const file = new File([bytes], response.data.fileName || 'downloaded.csi', { type: 'text/plain' });
                if (typeof setCsiFile === 'function') setCsiFile(file);
                if (typeof executeValidation === 'function') executeValidation(file);
            } else {
                toast.error(response.data.error || 'Failed to download CSI file');
            }
        } catch (error) {
            console.error('CSI Download Error:', error);
            toast.error(error.response?.data?.error || 'Failed to connect to automation server');
        } finally {
            setIsDownloadingCsi(false);
        }
    };

    const handleProfilePrefill = async () => {
        if (!prefillPassword) {
            toast.error('Portal password is required');
            return;
        }

        setIsExtractingProfile(true);
        try {
            const { success, data, error } = await extractProfileDataApi({
                tan: profileData.section1?.taxDeduction,
                password: prefillPassword
            });

            if (success && data) {
                const newData = { ...profileData };
                if (data.pan) {
                    newData.section1.permanentAccountNumber = data.pan;
                    if (newData.section3) newData.section3.permanentAccountNumber = data.pan;
                }
                if (data.name) {
                    newData.section2.name = data.name;
                }
                if (data.keyPerson) {
                    if (data.keyPerson.name) newData.section3.name = data.keyPerson.name;
                    if (data.keyPerson.pan) newData.section3.permanentAccountNumber = data.keyPerson.pan;
                }
                if (data.email) {
                    newData.section2.email = data.email;
                    if (newData.section3) newData.section3.email = data.email;
                }
                if (data.mobile && newData.section3) {
                    newData.section3.mobileNo = data.mobile;
                }

                if (data.address) {
                    newData.section2.flatNo = data.address.flatNo || '';
                    newData.section2.nameOfPremises = data.address.premisesName || '';
                    newData.section2.roadStreet = data.address.roadStreet || '';
                    newData.section2.areaLocality = data.address.areaLocality || '';
                    newData.section2.townCity = data.address.city || '';
                    newData.section2.pinCode = data.address.pinCode || '';

                    if (data.address.state) {
                        const upperState = data.address.state.toUpperCase().trim();
                        const matchedState = stateOptions.find(opt => opt === upperState) ||
                            stateOptions.find(opt => upperState.includes(opt) || opt.includes(upperState));

                        if (matchedState) {
                            newData.section2.state = matchedState;
                        }
                    }

                    if (newData.section3 && newData.section3.sameAsAbove) {
                        newData.section3.flatNo = newData.section2.flatNo;
                        newData.section3.nameOfPremises = newData.section2.nameOfPremises;
                        newData.section3.roadStreet = newData.section2.roadStreet;
                        newData.section3.areaLocality = newData.section2.areaLocality;
                        newData.section3.townCity = newData.section2.townCity;
                        newData.section3.pinCode = newData.section2.pinCode;
                        newData.section3.state = newData.section2.state;
                    }
                }

                setProfileData(newData);
                // Cloud sync via updateProfileData);
                toast.success('Profile data pre-filled successfully!');
                setShowPrefillPrompt(false);
                setPrefillPassword('');
            } else {
                toast.error(error || 'Extraction failed');
            }
        } catch (error) {
            toast.error('Extraction Failed: ' + (error.message));
        } finally {
            setIsExtractingProfile(false);
        }
    };

    const handleChallanDataChange = setChallanData;
    const handleTdsDataChange = setTdsData;

    const handleSectionChange = (section) => setActiveSection(section)
    const handleSidebarStateChange = useCallback((state) => setSidebarState(state), [])

    const today = new Date();
    const currMonth = today.getMonth();
    const currYear = today.getFullYear();
    const currentFyStart = currMonth >= 3 ? currYear : currYear - 1;

    const financialYearOptions = [];
    for (let start = currentFyStart; start >= 2023; start--) {
        financialYearOptions.push(`${start}-${(start + 1).toString().slice(-2)}`);
    }
    const quarterOptions = ['Q1', 'Q2', 'Q3', 'Q4'];
    const formOptions = ['27EQ', '143'];

    const handleClearData = () => {
        if (window.confirm('Are you sure you want to clear all data? This action cannot be undone.')) {
            const initialProfile = {
                section1: {
                    taxDeduction: '',
                    permanentAccountNumber: '',
                    typeOfDeduction: '',
                    receiptNoOriginal: '',
                    receiptNoEarlier: '',
                    isEarlierReturnFiled: 'No'
                },
                section2: {
                    name: '',
                    branchDivision: '',
                    flatNo: '',
                    nameOfPremises: '',
                    roadStreet: '',
                    areaLocality: '',
                    townCity: '',
                    pinCode: '',
                    state: '',
                    email: '',
                    stdCode: '',
                    telephoneNo: ''
                },
                section3: {
                    sameAsAbove: false,
                    name: '',
                    designation: '',
                    permanentAccountNumber: '',
                    nameOfPremises: '',
                    flatNo: '',
                    areaLocality: '',
                    roadStreet: '',
                    pinCode: '',
                    townCity: '',
                    telephone: '',
                    telephoneAlternate: '',
                    state: '',
                    email: '',
                    emailAlternate: '',
                    mobileNo: '',
                    hasAddressChanged: '',
                    hasRegularStatement: ''
                }
            }

            setChallanData([])
            setTdsData([])
            setOriginalTdsData([])
            setProfileData(initialProfile)

            localStorage.removeItem(getLegacyStorageKey('143', 'ChallanData'))
            localStorage.removeItem(getLegacyStorageKey('143', 'TdsData'))
            localStorage.removeItem(getLegacyStorageKey('143', 'ProfileData'))
            localStorage.removeItem(getLegacyStorageKey('143', 'OriginalTdsData'))

            toast.success('All data cleared successfully')
        }
    }

    const handleProfileInputChange = (field, value) => {
        const keys = field.split('.')
        const newData = JSON.parse(JSON.stringify(profileData || {}))
        let current = newData
        for (let i = 0; i < keys.length - 1; i++) {
            if (!current[keys[i]]) current[keys[i]] = {}
            current = current[keys[i]]
        }
        current[keys[keys.length - 1]] = value

        const isSame = (field === 'section3.sameAsAbove') ? value : newData.section3?.sameAsAbove
        if (isSame) {
            const s2 = newData.section2 || {}
            if (!newData.section3) newData.section3 = {}
            newData.section3.nameOfPremises = s2.nameOfPremises || ''
            newData.section3.flatNo = s2.flatNo || ''
            newData.section3.areaLocality = s2.areaLocality || ''
            newData.section3.roadStreet = s2.roadStreet || ''
            newData.section3.townCity = s2.townCity || ''
            newData.section3.pinCode = s2.pinCode || ''
            newData.section3.state = s2.state || ''
            newData.section3.email = s2.email || ''
            newData.section3.stdCode = s2.stdCode || ''
            newData.section3.telephone = s2.telephoneNo || ''
        }

        setProfileData(newData)
        // Cloud sync via updateProfileData)
    }

    const handleConsoFileUpload = (event) => {
        const file = event.target.files[0]
        if (file) {
            const reader = new FileReader()
            reader.onload = (e) => {
                const content = e.target.result
                const lines = content.split('\n')
                const bhLine = lines.find(l => l.split('^')[1] === 'BH')
                if (bhLine) {
                    const formInConso = (bhLine.split('^')[4] || '').toUpperCase().trim()
                    if (formInConso && formInConso !== '143' && formInConso !== '27EQ') {
                        toast.error(`Invalid Conso File! Expected Form 143, but file contains Form ${formInConso}.`)
                        alert(`Invalid Conso File!\n\nThis form is Form 143, but the uploaded Conso file belongs to Form ${formInConso}.\nPlease upload the correct Form 143 Conso file.`)
                        return
                    }
                }
                setConsoFileContent(content)
                toast.success('Conso file uploaded successfully')
            }
            reader.readAsText(file)
        }
    }

    return (
        <div className="min-h-screen bg-gray-50 w-full overflow-x-hidden">
            <ToastContainer position="top-right" autoClose={3000} />

            <TdsSidebar
                isOpen={sidebarOpen}
                isMobile={isMobile}
                onClose={() => setSidebarOpen(false)}
                activeSection={activeSection}
                onSectionChange={handleSectionChange}
                onSidebarStateChange={handleSidebarStateChange}
                tdsData={tdsData}
                challanData={challanData}
                formType="143"
            />

            <header className={`bg-white shadow-sm border-b transition-all duration-300 ${!isMobile ? (sidebarState.isExpanded ? 'ml-64' : 'ml-14') : ''}`} style={{
                width: !isMobile ? (sidebarState.isExpanded ? 'calc(100% - 16rem)' : 'calc(100% - 3.5rem)') : '100%'
            }}>
                <div className="w-full px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16 relative">
                        <div className="flex items-center">
                            <FileText className="w-6 h-6 mr-2 text-blue-600" />
                        </div>
                        <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
                            <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                Form 143 (TCS Return - FY 2026-27+)
                            </h1>
                        </div>
                        <button
                            onClick={handleClearData}
                            className="flex items-center text-red-600 hover:text-red-700 px-3 py-2 rounded-md text-sm font-medium hover:bg-red-50 transition-colors"
                        >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Clear Data
                        </button>
                    </div>
                </div>
            </header>

            <main className={`py-4 px-2 sm:px-4 lg:px-6 transition-all duration-300 ${!isMobile ? (sidebarState.isExpanded ? 'ml-64' : 'ml-14') : ''}`} style={{
                width: !isMobile ? (sidebarState.isExpanded ? 'calc(100% - 16rem)' : 'calc(100% - 3.5rem)') : '100%',
                maxWidth: '100%',
                overflowX: 'hidden',
                minHeight: 'calc(100vh - 4rem)'
            }}>
                <div className="p-4 md:p-8 pt-6">

                    {/* Selection Controls */}
                    <div className="bg-white rounded-lg shadow-sm p-4 mb-6 border border-gray-200">
                        <div className="flex flex-wrap gap-4 items-end">
                            <div className="flex-1 min-w-[180px] w-full">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Financial Year <span className="text-red-500">*</span></label>
                                <select
                                    value={selectedFinancialYear}
                                    onChange={(e) => {
                                        const newYear = e.target.value;
                                        setSelectedFinancialYear(newYear);
                                        localStorage.setItem('tdsFinancialYear', newYear);

                                        if (newYear) {
                                            const isNewFormat = parseInt(newYear.split('-')[0]) >= 2026;
                                            setSelectedForm(prevForm => {
                                                const f = prevForm;
                                                const mapped = isNewFormat
                                                    ? (f === '24Q' ? '138' : (f === '26Q' || f === '140') ? '140' : f === '27Q' ? '144' : f === '27EQ' ? '143' : f)
                                                    : (f === '138' ? '24Q' : (f === '140' || f === '26Q') ? '26Q' : f === '144' ? '27Q' : f === '143' ? '27EQ' : f);
                                                if (mapped !== prevForm) {
                                                    localStorage.setItem('tdsForm', mapped);
                                                    return mapped;
                                                }
                                                return prevForm;
                                            });
                                        }
                                    }}
                                    className="w-full px-3 py-2 border-2 border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50 text-gray-900"
                                >
                                    <option value="">Select FY</option>
                                    {financialYearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                                </select>
                            </div>

                            <div className="flex-1 min-w-[180px] w-full">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Quarter <span className="text-red-500">*</span></label>
                                <select
                                    value={selectedQuarter}
                                    onChange={(e) => {
                                        setSelectedQuarter(e.target.value)
                                        localStorage.setItem('tdsQuarter', e.target.value)
                                    }}
                                    className="w-full px-3 py-2 border-2 border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50 text-gray-900"
                                >
                                    <option value="">Select Quarter</option>
                                    {quarterOptions.map(q => <option key={q} value={q}>{q}</option>)}
                                </select>
                            </div>

                            <div className="flex-1 min-w-[180px] w-full">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Form Type <span className="text-red-500">*</span></label>
                                <select
                                    value={selectedForm}
                                    onChange={(e) => {
                                        const newForm = e.target.value;
                                        setSelectedForm(newForm)
                                        localStorage.setItem('tdsForm', newForm)
                                        const targetPath = getTdsFormRoute(newForm, selectedReturnType, selectedFinancialYear)
                                        navigate(targetPath)
                                    }}
                                    className="w-full px-3 py-2 border-2 border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50 text-gray-900"
                                >
                                    {['24Q', '26Q', '27Q', '27EQ'].map(formVal => {
                                        const f = formVal;
                                        const actualForm = selectedFinancialYear && parseInt(selectedFinancialYear.split('-')[0]) >= 2026
                                            ? (f === '24Q' ? '138' : (f === '26Q' || f === '140') ? '140' : f === '27Q' ? '144' : f === '27EQ' ? '143' : f)
                                            : (f === '138' ? '24Q' : (f === '140' || f === '26Q') ? '26Q' : f === '144' ? '27Q' : f === '143' ? '27EQ' : f);
                                        return (
                                            <option key={formVal} value={actualForm}>
                                                {actualForm}
                                            </option>
                                        )
                                    })}
                                </select>
                            </div>

                            <div className="flex-1 min-w-[180px] w-full">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Return Type <span className="text-red-500">*</span></label>
                                <select
                                    value={selectedReturnType}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setSelectedReturnType(val)
                                        localStorage.setItem('SELECTED_RETURN_TYPE', val)
                                        const targetPath = getTdsFormRoute(selectedForm, val, selectedFinancialYear)
                                        navigate(targetPath)
                                    }}
                                    className="w-full px-3 py-2 border-2 border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50 text-gray-900"
                                >
                                    <option value="">Select Type</option>
                                    <option value="Regular">Regular</option>
                                    <option value="Correction">Correction</option>
                                </select>
                            </div>

                            <div className="flex-shrink-0">
                                <button
                                    onClick={() => {
                                        const targetForm = selectedForm ? selectedForm.toLowerCase() : '143';
                                        const isCorrection = selectedReturnType === 'Correction';
                                        const suffix = isCorrection ? '-correction' : '';
                                        const targetPath = `/tds-dashboard/${targetForm}${suffix}`;
                                        if (location.pathname !== targetPath) {
                                            navigate(targetPath);
                                            toast.success('Selection Applied');
                                        } else {
                                            toast.success('Selection Applied');
                                        }
                                    }}
                                    className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium shadow-md"
                                >
                                    Apply Selection
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Statement Receipt Details Section (Conditional) */}
                    {(selectedReturnType === 'Correction' || selectedReturnType === 'Regular') && (
                        <div className="bg-white rounded-xl shadow-md p-6 mb-8 border border-gray-100 overflow-hidden relative">
                            <div className="absolute top-0 left-0 w-2 h-full bg-blue-500"></div>
                            <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center">
                                <Building2 className="w-5 h-5 mr-2 text-blue-600" />
                                Statement Receipt Details <span className="text-red-500 ml-1">*</span>
                            </h3>

                            <div className="space-y-6">
                                {selectedReturnType === 'Correction' && (
                                    <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
                                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                                            <div className="lg:col-span-1">
                                                <div className="flex flex-col items-center">
                                                    <input
                                                        type="file"
                                                        accept=".tds"
                                                        onChange={handleConsoFileUpload}
                                                        className="hidden"
                                                        id="conso-upload-143"
                                                    />
                                                    <label
                                                        htmlFor="conso-upload-143"
                                                        className="group relative flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-blue-300 rounded-2xl bg-white hover:bg-blue-50 hover:border-blue-500 transition-all cursor-pointer overflow-hidden shadow-sm"
                                                    >
                                                        {consoFileContent ? (
                                                            <motion.div initial={{ scale: 0.8 }} animate={{ scale: 1 }} className="flex flex-col items-center text-green-600">
                                                                <div className="bg-green-100 p-3 rounded-full mb-2">
                                                                    <FileText className="w-8 h-8" />
                                                                </div>
                                                                <span className="text-sm font-bold">Conso File Ready</span>
                                                                <span className="text-xs text-green-500 mt-1">Click to Replace</span>
                                                            </motion.div>
                                                        ) : (
                                                            <div className="flex flex-col items-center text-gray-500">
                                                                <div className="bg-blue-100 p-3 rounded-full mb-3 group-hover:bg-blue-200 transition-colors">
                                                                    <Upload className="w-8 h-8 text-blue-600" />
                                                                </div>
                                                                <span className="text-sm font-bold text-gray-700 group-hover:text-blue-700">Upload Conso File</span>
                                                                <span className="text-[10px] text-gray-400 mt-1 uppercase tracking-wider font-medium">Click to browse (.tds)</span>
                                                            </div>
                                                        )}
                                                    </label>
                                                </div>
                                            </div>

                                            <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div className="space-y-2">
                                                    <label className="text-xs font-bold text-blue-700 uppercase tracking-wider">
                                                        Receipt No. of Original Return
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={profileData?.section1?.receiptNoOriginal || ''}
                                                        disabled
                                                        className="w-full px-4 py-3 bg-white border border-blue-200 rounded-xl text-gray-700 font-medium shadow-sm cursor-not-allowed"
                                                        placeholder="Waiting for Conso upload..."
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="text-xs font-bold text-blue-700 uppercase tracking-wider">
                                                        Receipt No. of Earlier Statement
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={profileData?.section1?.receiptNoEarlier || ''}
                                                        disabled
                                                        className="w-full px-4 py-3 bg-white border border-blue-200 rounded-xl text-gray-700 font-medium shadow-sm cursor-not-allowed"
                                                        placeholder="Waiting for Conso upload..."
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {selectedReturnType === 'Regular' && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Is earlier Return Filed?
                                            </label>
                                            <select
                                                value={profileData?.section1?.isEarlierReturnFiled || 'No'}
                                                onChange={(e) => handleProfileInputChange('section1.isEarlierReturnFiled', e.target.value)}
                                                className="w-full px-3 py-2 border-2 border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
                                            >
                                                <option value="No">No</option>
                                                <option value="Yes">Yes</option>
                                            </select>
                                        </div>
                                        {profileData?.section1?.isEarlierReturnFiled === 'Yes' && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Receipt No. of Original Return <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={profileData?.section1?.receiptNoOriginal || ''}
                                                    onChange={(e) => handleProfileInputChange('section1.receiptNoOriginal', e.target.value)}
                                                    required
                                                    className="w-full px-3 py-2 border-2 border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50 text-gray-900"
                                                    placeholder="Enter 15-digit Receipt Number"
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="bg-white rounded-lg shadow-lg border border-gray-200 min-h-[600px] flex flex-col">

                        {activeSection === 'tds-details' && (
                            <div className="p-4 flex-1">
                                <div className="flex justify-between items-center mb-4">
                                    <h2 className="text-lg font-bold text-gray-800">TCS Collectee Details (Form 143)</h2>
                                    {tdsData.length > 0 && (
                                        <span className="text-sm font-medium text-gray-600 bg-gray-100 px-3 py-1 rounded-full border border-gray-200">
                                            Total Records: {tdsData.length} | Total Tax: ₹{tdsData.reduce((sum, item) => sum + parseFloat(item.tdsAmount || item.totalTaxDeposited || 0), 0).toLocaleString('en-IN')}
                                        </span>
                                    )}
                                </div>

                                <TdsDetailsTable
                                    tdsData={tdsData}
                                    challanData={challanData}
                                    setTdsData={handleTdsDataChange}
                                    onTdsDataChange={handleTdsDataChange}
                                    selectedFinancialYear={selectedFinancialYear}
                                    selectedQuarter={selectedQuarter}
                                    isRevised={selectedReturnType === 'Correction'}
                                    originalData={originalTdsData}
                                    sectionOptions={form143Sections}
                                    is27EQ={true}
                                    collecteeOptions={collecteeCodes143}
                                    formType="143"
                                />
                            </div>
                        )}

                        {activeSection === 'challan-details' && (
                            <div className="p-4 flex-1">
                                <div className="flex justify-between items-center mb-4">
                                    <h2 className="text-lg font-bold text-gray-800">Challan Details</h2>
                                    {challanData.length > 0 && (
                                        <span className="text-sm font-medium text-gray-600 bg-gray-100 px-3 py-1 rounded-full border border-gray-200">
                                            Total Challans: {challanData.length} | Total Tax: ₹{challanData.reduce((sum, item) => sum + parseFloat(item.totalAmount || item.challanAmount || 0), 0).toLocaleString('en-IN')}
                                        </span>
                                    )}
                                </div>

                                <ChallanDetailsTable
                                    challanData={challanData}
                                    tdsData={tdsData}
                                    setChallanData={handleChallanDataChange}
                                    onChallanDataChange={handleChallanDataChange}
                                    tan={profileData?.section1?.taxDeduction}
                                    financialYear={selectedFinancialYear}
                                    isRevised={selectedReturnType === 'Correction'}
                                    minorHeadOptions={minorHeadCodes143}
                                    sectionOptions={form143Sections}
                                    formType="143"
                                />
                            </div>
                        )}

                        {activeSection === 'profile' && (
                            <TdsProfileSection
                                profileData={profileData}
                                isProfileEditing={isProfileEditing}
                                onProfileDataChange={handleProfileInputChange}
                                onPrefillClick={() => setShowPrefillPrompt(true)}
                                selectedReturnType={selectedReturnType}
                            />
                        )}

                        {activeSection === 'generated-files' && (
                            <TdsGeneratedFiles isEmbedded={true} />
                        )}

                    </div>
                </div>
            </main>

            {/* Profile Pre-fill Prompt Modal */}
            {
                showPrefillPrompt && (
                    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-900 bg-opacity-50 backdrop-blur-sm">
                        <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md transform transition-all scale-100">
                            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                                <User className="w-5 h-5 mr-2 text-blue-600" />
                                Pre-fill Profile Data
                            </h3>

                            <p className="text-gray-600 mb-4 text-sm">
                                We will automatically extract your PAN, Email, Mobile and Address from the Income Tax Portal.
                            </p>

                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Income Tax Portal Password</label>
                                <div className="relative">
                                    <input
                                        type={showPrefillPassword ? "text" : "password"}
                                        className="w-full border border-gray-300 rounded-md p-2 pr-10 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                        value={prefillPassword}
                                        onChange={(e) => setPrefillPassword(e.target.value)}
                                        placeholder="Enter Portal Password"
                                        autoFocus
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPrefillPassword(!showPrefillPassword)}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                                    >
                                        {showPrefillPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">TAN: {profileData?.section1?.taxDeduction}</p>
                            </div>

                            <div className="flex space-x-3">
                                <button
                                    onClick={handleProfilePrefill}
                                    disabled={isExtractingProfile || !prefillPassword}
                                    className={`flex-1 bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700 transition flex items-center justify-center border border-transparent shadow-md ${(isExtractingProfile || !prefillPassword) ? 'opacity-70 cursor-not-allowed' : ''}`}
                                >
                                    {isExtractingProfile ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                            Get Data...
                                        </>
                                    ) : (
                                        <>
                                            <Upload className="w-4 h-4 mr-2" />
                                            Get Data
                                        </>
                                    )}
                                </button>
                                <button
                                    onClick={() => {
                                        setShowPrefillPrompt(false);
                                        setPrefillPassword('');
                                    }}
                                    disabled={isExtractingProfile}
                                    className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 transition"
                                >
                                    Cancel
                                </button>
                            </div>
                            {!isExtractingProfile && (
                                <button
                                    onClick={() => setShowPrefillPrompt(false)}
                                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            )}
                        </div>
                    </div>
                )
            }
            {/* CSI Confirmation Modal */}
            {
                showCsiUploadPopup && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
                        <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl animate-fade-in border border-gray-100">
                            <h3 className="text-xl font-bold mb-4 text-gray-800 border-b pb-2">Upload CSI File</h3>
                            <p className="text-gray-600 mb-6 leading-relaxed">
                                To proceed with validation, you need to upload the <strong>Challan Status Inquiry (CSI)</strong> file.
                                <br /><br />
                                Please ensure you have downloaded it from the Income Tax portal.
                            </p>
                            <div className="flex justify-end space-x-4">
                                <button
                                    onClick={() => setShowCsiUploadPopup(false)}
                                    className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-medium transition-colors duration-200"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        setShowCsiUploadPopup(false)
                                        if (csiFileInputRef.current) {
                                            csiFileInputRef.current.value = ''
                                            csiFileInputRef.current.click()
                                        }
                                    }}
                                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center transition-colors duration-200 shadow-md"
                                >
                                    <Upload className="w-4 h-4 mr-2" />
                                    Upload CSI File
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            <input
                type="file"
                ref={csiFileInputRef}
                style={{ display: 'none' }}
                accept=".csi"
                onChange={handleCsiFileChange}
            />

            {/* CSI Download Prompt Modal */}
            {
                showDownloadPrompt && (
                    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-900 bg-opacity-50 backdrop-blur-sm">
                        <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md transform transition-all scale-100">
                            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                                <Upload className="w-5 h-5 mr-2 text-blue-600" />
                                Download CSI File
                            </h3>

                            <p className="text-gray-600 mb-4 text-sm">
                                Would you like to automatically download the latest CSI file from the Income Tax Portal?
                            </p>

                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Income Tax Password</label>
                                <div className="relative">
                                    <input
                                        type={showDownloadPassword ? "text" : "password"}
                                        className="w-full border border-gray-300 rounded-md p-2 pr-10 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                        value={downloadPassword}
                                        onChange={(e) => setDownloadPassword(e.target.value)}
                                        placeholder="Enter Password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowDownloadPassword(!showDownloadPassword)}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                                    >
                                        {showDownloadPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">TAN: {profileData?.section1?.taxDeduction}</p>
                            </div>

                            <div className="flex space-x-3">
                                <button
                                    onClick={handleCsiDownload}
                                    disabled={isDownloadingCsi}
                                    className={`flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition flex items-center justify-center border border-transparent shadow-md ${isDownloadingCsi ? 'opacity-70 cursor-not-allowed' : ''}`}
                                >
                                    {isDownloadingCsi ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                                            Downloading...
                                        </>
                                    ) : (
                                        <span className="font-medium">Download & Continue</span>
                                    )}
                                </button>
                                <button
                                    onClick={handleSkipDownload}
                                    disabled={isDownloadingCsi}
                                    className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 transition"
                                >
                                    Skip (I have file)
                                </button>
                            </div>
                            <button
                                onClick={() => setShowDownloadPrompt(false)}
                                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                )
            }

            {/* Validation Lock Screen Overlay */}
            {
                isValidating && (
                    <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-gray-900 bg-opacity-80 backdrop-blur-sm cursor-wait">
                        <div className="bg-white p-8 rounded-2xl shadow-2xl flex flex-col items-center max-w-lg text-center transform transition-all scale-100">
                            <div className="relative mb-6">
                                <div className="absolute inset-0 bg-orange-100 rounded-full animate-ping opacity-75"></div>
                                <div className="relative bg-white rounded-full p-2">
                                    <div className="animate-spin rounded-full h-16 w-16 border-4 border-orange-200 border-t-orange-600"></div>
                                </div>
                            </div>

                            <h3 className="text-2xl font-bold text-gray-800 mb-2">Validation in Progress</h3>
                            <p className="text-gray-600 mb-6 text-sm">
                                Validating your Form 143 return against CSI file...
                            </p>

                            <button
                                onClick={handleCancelValidation}
                                className="px-6 py-2 bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 rounded-lg text-sm font-semibold transition"
                            >
                                Cancel Validation
                            </button>
                        </div>
                    </div>
                )
            }
        </div>
    )
}

export default Tds143Form
